import React, { useEffect, useState } from "react";
import { Flag, ScrollText, Bird, Flower2, TreePine, Rabbit, Fish, Bug, Music } from "lucide-react";
import { getStateSymbols } from "@/lib/api";
import { T } from "@/context/LanguageContext";
import { SourceBadge } from "@/components/site/SourceBadge";
import { InlineListen } from "@/components/site/ListenControl";

const SYM_ICON = {
  nickname: Flag, motto: ScrollText, bird: Bird, flower: Flower2, tree: TreePine,
  mammal: Rabbit, fish: Fish, insect: Bug, song: Music,
};

/**
 * Official State Symbols — a source-governed module (flag/seal motto, bird,
 * flower, tree, animal, fish, insect). Every fact carries explicit
 * provenance; nothing is AI-generated. Recovered as a legacy USAStates.com
 * category and independently re-sourced from state statutes.
 */
export function StateSymbols({ geoId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    getStateSymbols(geoId).then((d) => { if (alive) setData(d); }).catch(() => {});
    return () => { alive = false; };
  }, [geoId]);

  if (!data || !data.available || !data.symbols?.length) return null;

  const song = data.symbols.find((s) => s.key === "song");

  return (
    <section className="scroll-mt-28" id="symbols" data-testid="module-symbols">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Flag className="h-5 w-5 text-oldglory-red" />
          <h3 className="font-display text-xl font-black tracking-tight text-navy sm:text-2xl">
            <T>State Symbols</T>
          </h3>
        </div>
        {song && (
          <InlineListen
            paragraphs={[`The official state song of ${data.name} is "${song.value}".`]}
            label={`Listen: ${song.value}`}
            testid="state-song-listen"
          />
        )}
      </div>
      <p className="mb-5 max-w-2xl text-sm text-muted-foreground">
        <T>Official symbols of</T> {data.name}
        <T>, sourced from state statutes. Each fact is verified — not AI-generated.</T>
      </p>

      {(data.flag || data.seal) && (
        <div className="mb-6 flex flex-wrap gap-4" data-testid="symbols-media">
          {data.flag?.img && (
            <figure className="flex flex-col items-center gap-1.5" data-testid="symbol-flag">
              <img src={data.flag.img} alt={`Flag of ${data.name}`} loading="lazy"
                   className="h-24 w-auto rounded-md border border-border object-contain shadow-sm" />
              <figcaption className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"><T>Official Flag</T></figcaption>
            </figure>
          )}
          {data.seal?.img && (
            <figure className="flex flex-col items-center gap-1.5" data-testid="symbol-seal">
              <img src={data.seal.img} alt={`Seal of ${data.name}`} loading="lazy"
                   className="h-24 w-24 rounded-md border border-border bg-white object-contain p-1 shadow-sm" />
              <figcaption className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"><T>Official Seal</T></figcaption>
            </figure>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="symbols-grid">
        {data.symbols.map((s) => {
          const Icon = SYM_ICON[s.key] || Flag;
          return (
            <div
              key={s.key}
              className="flex flex-col rounded-xl border border-border bg-white p-4 transition-all hover:border-navy/30 hover:shadow-md"
              data-testid={`symbol-${s.key}`}
            >
              <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                <Icon className="h-4 w-4 text-oldglory-red/80" /> <T>{s.label}</T>
              </div>
              <div className="mt-1.5 font-display text-base font-bold leading-tight text-navy">
                {s.value}
              </div>
              {s.note && (
                <div className="mt-0.5 text-xs italic text-muted-foreground">&ldquo;{s.note}&rdquo;</div>
              )}
              <div className="mt-auto pt-2.5">
                <SourceBadge source={{ label: s.source }} verification={s.verification} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default StateSymbols;
